import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { SEO } from "@/components/common/SEO";

/**
 * Minimal "not found" state for detail pages (artist / artwork / event).
 * Matches the gallery's editorial aesthetic — sparse, navy, gold accent.
 *
 * @param {string} subject      e.g. "artist", "artwork", "event"
 * @param {string} backTo       href to return to (e.g. "/artists")
 * @param {string} backLabel    button label (e.g. "All Artists")
 * @param {string} [message]    optional custom body copy
 * @param {boolean} [isError]   if true, shows "Something went wrong" copy instead of "not found"
 */
export function NotFound({ subject, backTo, backLabel, message, isError = false }) {
  const title = isError
    ? "Something went wrong"
    : `We couldn't find that ${subject}`;
  const body =
    message ||
    (isError
      ? "There was a problem loading this page. Please try again in a moment."
      : `This ${subject} may have been removed, or the link may be broken.`);

  return (
    <div
      data-testid={`not-found-${subject}`}
      className="pt-20 min-h-screen flex items-center justify-center px-6"
    >
      <SEO
        title={`${title} | Rain Boy Fine Art`}
        description={body}
        path=""
        noindex
      />
      <div className="max-w-xl w-full text-center py-20 md:py-28">
        <p className="overline mb-6">{isError ? "Error" : "Not Found"}</p>
        <h1
          className="section-heading font-heading text-[#0A1128] mb-6"
          style={{ fontSize: "clamp(2.25rem, 4vw, 3.5rem)", lineHeight: 1.05 }}
        >
          {title}
        </h1>
        <div className="w-10 h-[1px] bg-[#CBA365] mx-auto mb-6" />
        <p
          className="text-[#4A5568] mb-10 mx-auto"
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: "1rem",
            lineHeight: 1.65,
            maxWidth: "30rem",
          }}
        >
          {body}
        </p>
        <Link
          to={backTo}
          className="group inline-flex items-center gap-2"
          data-testid="not-found-back-link"
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 500,
            fontSize: "0.72rem",
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            color: "#4A5568",
            textDecoration: "none",
          }}
        >
          <ArrowLeft
            size={13}
            className="transition-transform duration-300 group-hover:-translate-x-1 group-hover:text-[#CBA365]"
          />
          <span className="border-b border-[#CBA365] pb-[2px] transition-colors duration-300 group-hover:text-[#CBA365]">
            {backLabel}
          </span>
        </Link>
      </div>
    </div>
  );
}
